import { dedupeIssues, SEVERITY_ORDER } from '@insio/validation';
import type { BannerIssue, GroupedRow, SeverityGroup } from './types';

/**
 * Splits a flat list of `BannerIssue`s into one `SeverityGroup` per severity
 * (errors, warnings, then info), each carrying its flat (non-grouped) issues
 * plus per-`groupId` clusters.
 *
 * - All issues are first deduplicated via `dedupeIssues` (shared with
 *   `FieldErrorList`): issues with the same `path` + `code` collapse
 *   into the most severe one, so a warning is dropped when an identical
 *   error exists. Two issues with the same code but different paths are
 *   kept — they refer to different fields.
 * - Grouped issues are sorted by `groupId` using natural numeric ordering so
 *   `invoiceLines.10` lands after `invoiceLines.2`.
 * - Severities with no issues are dropped.
 */
export function buildSeverityGroups(allIssues: BannerIssue[]): SeverityGroup[] {
  const effectiveIssues = dedupeIssues(allIssues);

  return SEVERITY_ORDER.map(severity => {
    const sevIssues = effectiveIssues.filter(i => i.severity === severity);
    const groupsMap = new Map<string, GroupedRow>();
    const flatIssues: BannerIssue[] = [];

    for (const issue of sevIssues) {
      if (issue.groupId) {
        const existing = groupsMap.get(issue.groupId);
        if (existing) {
          existing.issues.push(issue);
        } else {
          groupsMap.set(issue.groupId, {
            groupId: issue.groupId,
            groupLabel: issue.groupLabel ?? issue.groupId,
            issues: [issue]
          });
        }
      } else {
        flatIssues.push(issue);
      }
    }

    const groups = Array.from(groupsMap.values()).sort((a, b) =>
      a.groupId.localeCompare(b.groupId, undefined, { numeric: true })
    );

    return { severity, flatIssues, groups, count: flatIssues.length + groups.length };
  }).filter(g => g.count > 0);
}
